import React, { useState, useEffect, useRef } from 'react';
import { Plus, Archive, Eye, EyeOff, FileText, Upload, X, Copy, Search, Trash2, CheckCircle } from 'lucide-react';
import { fetchProducts, createProduct, updateProduct, deleteProduct, searchTestingResults, fetchActiveCustomers } from '../lib/api';
import { isAdmin } from '../lib/auth';
import type { Product } from '../lib/types';

interface Customer {
  id: string;
  companyName: string;
}

const formatCurrency = (amount: string | number): string => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return numericAmount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const INITIAL_PRODUCT_STATE: Product = {
  id: '',
  product: '',
  sku: '',
  batchNumber: '',
  brand: '',
  strain: '',
  lineage: '',
  dominance: '',
  totalCbd: '',
  totalThc: '',
  totalCannabinoids: '',
  images: [],
  price: '',
  unitSize: '',
  testingResults: '',
  qoh: '',
  isExclusive: false,
  exclusiveCustomers: [],
  status: 'active',
  archived: false,
  category: ''
};

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showCopyFrom, setShowCopyFrom] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentProduct, setCurrentProduct] = useState<Product>(INITIAL_PRODUCT_STATE);

  useEffect(() => {
    loadProducts();
    loadCustomers();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await fetchProducts();
      setProducts(data);
      setError(null);
    } catch (err) {
      setError('Failed to load products');
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await fetchActiveCustomers();
      setCustomers(data);
    } catch (err) {
      console.error('Error loading customers:', err);
      // Don't set error state here to avoid blocking product loading
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setCurrentProduct(INITIAL_PRODUCT_STATE);
  };

  const handleBatchNumberChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const batchNumber = e.target.value;
    setCurrentProduct({ ...currentProduct, batchNumber });
    
    if (batchNumber.length > 0) {
      const pdfUrl = await searchTestingResults(batchNumber);
      if (pdfUrl) {
        setCurrentProduct(prev => ({ ...prev, testingResults: pdfUrl }));
      }
    }
  };

  const copyFromProduct = (sourceProduct: Product) => {
    setCurrentProduct({
      ...currentProduct,
      product: sourceProduct.product,
      brand: sourceProduct.brand,
      strain: sourceProduct.strain,
      lineage: sourceProduct.lineage,
      dominance: sourceProduct.dominance,
      price: sourceProduct.price,
      unitSize: sourceProduct.unitSize,
      images: [...sourceProduct.images],
      category: sourceProduct.category
    });
    setShowCopyFrom(false);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxFiles = 4;
    const remainingSlots = maxFiles - currentProduct.images.length;

    if (files.length > remainingSlots) {
      alert(`You can only upload ${remainingSlots} more image${remainingSlots !== 1 ? 's' : ''}`);
      return;
    }

    // Upload each file to MinIO
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('sku', currentProduct.sku);
        formData.append('index', String(currentProduct.images.length + i));

        const response = await fetch('/api/upload.php', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(
            response.status === 413
              ? 'Image is too large. Please use a smaller image.'
              : `Upload failed (HTTP ${response.status})`
          );
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Failed to upload image');
        }

        // Add MinIO URL to images array
        setCurrentProduct(prev => ({
          ...prev,
          images: [...prev.images, result.url]
        }));

      } catch (err) {
        console.error('Error uploading image:', err);
        setError(`Failed to upload image: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = async (index: number) => {
    const imageUrl = currentProduct.images[index];

    // Only delete from MinIO if it's a URL (not base64)
    if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
      try {
        const response = await fetch('/api/deleteImage.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ url: imageUrl })
        });

        const result = await response.json();

        if (!result.success) {
          console.error('Failed to delete image from MinIO:', result.error);
          // Continue with removal from state even if MinIO deletion fails
        }
      } catch (err) {
        console.error('Error deleting image from MinIO:', err);
        // Continue with removal from state even if MinIO deletion fails
      }
    }

    // Remove from local state
    setCurrentProduct(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentProduct.batchNumber?.trim()) {
      setError('Batch number is required');
      return;
    }

    if (!currentProduct.qoh) {
      setError('Quantity on Hand is required');
      return;
    }
    
    try {
      if (currentProduct.id) {
        const updatedProduct = await updateProduct(currentProduct);
        setProducts(products.map(p => p.id === updatedProduct.id ? updatedProduct : p));
      } else {
        const newProduct = await createProduct(currentProduct);
        setProducts([...products, newProduct]);
      }
      closeForm();
    } catch (err) {
      setError('Failed to save product');
      console.error('Error saving product:', err);
    }
  };

  const toggleArchived = async (id: string) => {
    try {
      const product = products.find(p => p.id === id);
      if (!product) return;

      const updatedProduct = await updateProduct({
        ...product,
        archived: !product.archived,
        status: !product.archived ? 'inactive' : product.status
      });

      setProducts(products.map(p => p.id === updatedProduct.id ? updatedProduct : p));
    } catch (err) {
      setError('Failed to update product archive status');
      console.error('Error updating product archive status:', err);
    }
  };

  const handleDeleteProduct = (id: string) => {
    const product = products.find(p => p.id === id);
    if (!product) return;

    setProductToDelete(product);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;

    try {
      const result = await deleteProduct(productToDelete.id);

      if (result.action === 'deleted') {
        // Product was actually deleted - remove from list
        setProducts(products.filter(p => p.id !== productToDelete.id));
        closeForm(); // Close the edit modal and reset state
        setShowDeleteConfirm(false); // Close the delete confirm modal
        setSuccessMessage(result.message);
        setTimeout(() => setSuccessMessage(null), 5000); // Auto-dismiss after 5 seconds
      } else if (result.action === 'inactivated') {
        // Product has order history - was set to inactive instead
        const updatedProduct = products.find(p => p.id === productToDelete.id);
        if (updatedProduct) {
          updatedProduct.status = 'inactive';
          updatedProduct.archived = true;
          setProducts([...products]);
        }
        closeForm(); // Close the edit modal and reset state
        setShowDeleteConfirm(false); // Close the delete confirm modal
        setSuccessMessage(result.message);
        setTimeout(() => setSuccessMessage(null), 5000); // Auto-dismiss after 5 seconds
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete product');
      console.error('Error deleting product:', err);
      setShowDeleteConfirm(false); // Close the delete confirm modal on error
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setProductToDelete(null);
  };

  const filteredProducts = products
    .filter(p => !p.archived || showArchived)
    .filter(p => p.status === 'active' || showInactive)
    .filter(p => {
      const searchLower = searchTerm.toLowerCase();
      return (
        p.product.toLowerCase().includes(searchLower) ||
        p.sku.toLowerCase().includes(searchLower) ||
        p.brand.toLowerCase().includes(searchLower) ||
        p.strain.toLowerCase().includes(searchLower) ||
        p.category.toLowerCase().includes(searchLower)
      );
    });

  if (loading) {
    return <div className="text-center py-8">Loading products...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        {error}
        <button
          onClick={loadProducts}
          className="block mx-auto mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 max-w-md animate-slide-in">
          <div className="bg-green-50 border-l-4 border-green-500 rounded-lg shadow-lg p-4 flex items-start space-x-3">
            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-800 font-medium">{successMessage}</p>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-600 hover:text-green-800 flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
        <h2 className="text-2xl font-bold text-gray-800">Products</h2>
        <div className="flex items-center space-x-4">
          <div className="relative flex-1 sm:flex-none sm:min-w-[300px]">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center space-x-2 text-gray-600 hover:text-green-600"
          >
            {showArchived ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            <span className="hidden sm:inline">{showArchived ? 'Hide Archived' : 'Show Archived'}</span>
          </button>
          <button
            onClick={() => setShowInactive(!showInactive)}
            className="flex items-center space-x-2 text-gray-600 hover:text-green-600"
          >
            {showInactive ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            <span className="hidden sm:inline">{showInactive ? 'Hide Inactive' : 'Show Inactive'}</span>
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 transition duration-200"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Add Product</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.map((product) => (
          <div key={product.id} className={`bg-white rounded-lg shadow-md p-6 flex flex-col ${product.archived ? 'opacity-75' : ''}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold">{product.product}</h3>
                  <button
                    onClick={() => toggleArchived(product.id)}
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.archived
                        ? 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        : product.status === 'active'
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                    }`}
                  >
                    {product.archived ? 'Archived' : product.status === 'active' ? 'Active' : 'Inactive'}
                  </button>
                  {product.isExclusive && (
                    <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                      Exclusive
                    </span>
                  )}
                </div>
                <p className="text-gray-600">{product.brand}</p>
                <div className="mt-1 space-y-1">
                  <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                  <p className="text-sm text-gray-500">Batch: {product.batchNumber}</p>
                  <p className="text-sm text-gray-500">Category: {product.category}</p>
                  <p className="text-sm text-gray-500">Unit Size: {product.unitSize || 'Not specified'}</p>
                  {product.testingResults && (
                    <a
                      href={product.testingResults}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-green-600 hover:text-green-700 text-sm"
                    >
                      <FileText className="w-4 h-4" />
                      <span>View Testing Results</span>
                    </a>
                  )}
                </div>
              </div>
              <span className="text-green-600 font-semibold">{formatCurrency(product.price)}</span>
            </div>
            
            {product.images.length > 0 && (
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-2">
                  {product.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`${product.product} ${index + 1}`}
                      className="w-full h-32 object-contain rounded-lg bg-white"
                    />
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex-1 space-y-2">
              <p><span className="font-medium">Strain:</span> {product.strain}</p>
              {product.lineage && (
                <p><span className="font-medium">Lineage:</span> {product.lineage}</p>
              )}
              <p><span className="font-medium">Dominance:</span> {product.dominance}</p>
              <p><span className="font-medium">Total THC:</span> {product.totalThc}{product.category === 'Edible' ? 'mg' : '%'}</p>
              <p><span className="font-medium">Total CBD:</span> {product.totalCbd}{product.category === 'Edible' ? 'mg' : '%'}</p>
              <p><span className="font-medium">Total Cannabinoids:</span> {product.totalCannabinoids}{product.category === 'Edible' ? 'mg' : '%'}</p>
              <p><span className="font-medium">Quantity on Hand:</span> {product.qoh}</p>
              {product.isExclusive && product.exclusiveCustomers.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium text-gray-700">Available to:</p>
                  <div className="mt-1 space-y-1">
                    {product.exclusiveCustomers.map(customerId => {
                      const customer = customers.find(c => c.id === customerId);
                      return customer && (
                        <p key={customer.id} className="text-sm text-gray-600">
                          {customer.companyName}
                        </p>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-auto pt-4 border-t">
              <button
                onClick={() => {
                  setCurrentProduct(product);
                  setShowForm(true);
                }}
                className="w-full text-center text-green-600 hover:text-green-700 font-medium"
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold">
                {currentProduct.id ? 'Edit Product' : 'Add New Product'}
              </h3>
              {!currentProduct.id && (
                <button
                  type="button"
                  onClick={() => setShowCopyFrom(true)}
                  className="flex items-center space-x-2 text-green-600 hover:text-green-700"
                >
                  <Copy className="w-4 h-4" />
                  <span>Copy from Existing</span>
                </button>
              )}
            </div>

            {showCopyFrom && (
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-2">Copy from Existing Product</h4>
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => copyFromProduct(product)}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-100 text-left"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{product.product}</div>
                        <div className="text-sm text-gray-500">
                          SKU: {product.sku} • Brand: {product.brand}
                        </div>
                      </div>
                      <Copy className="w-4 h-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name
                  </label>
                  <input
                    type="text"
                    value={currentProduct.product}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, product: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SKU
                  </label>
                  <input
                    type="text"
                    value={currentProduct.sku}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, sku: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={currentProduct.category}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, category: e.target.value as Product['category'] })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="Concentrate">Concentrate</option>
                    <option value="Cartridge">Cartridge</option>
                    <option value="Edible">Edible</option>
                    <option value="Flower">Flower</option>
                    <option value="Preroll">Preroll</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Batch Number
                  </label>
                  <input
                    type="text"
                    value={currentProduct.batchNumber}
                    onChange={handleBatchNumberChange}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brand
                  </label>
                  <input
                    type="text"
                    value={currentProduct.brand}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, brand: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Strain
                  </label>
                  <input
                    type="text"
                    value={currentProduct.strain}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, strain: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lineage
                  </label>
                  <input
                    type="text"
                    value={currentProduct.lineage}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, lineage: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dominance
                  </label>
                  <select
                    value={currentProduct.dominance}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, dominance: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="">Select Dominance</option>
                    <option value="Indica">Indica</option>
                    <option value="Indica Hybrid">Indica Hybrid</option>
                    <option value="Sativa">Sativa</option>
                    <option value="Sativa Hybrid">Sativa Hybrid</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total CBD {currentProduct.category === 'Edible' ? '(mg)' : '(%)'}
                  </label>
                  <input
                    type="text"
                    value={currentProduct.totalCbd}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, totalCbd: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total THC {currentProduct.category === 'Edible' ? '(mg)' : '(%)'}
                  </label>
                  <input
                    type="text"
                    value={currentProduct.totalThc}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, totalThc: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Cannabinoids {currentProduct.category === 'Edible' ? '(mg)' : '(%)'}
                  </label>
                  <input
                    type="text"
                    value={currentProduct.totalCannabinoids}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, totalCannabinoids: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price
                  </label>
                  <input
                    type="text"
                    value={currentProduct.price}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, price: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit Size
                  </label>
                  <input
                    type="text"
                    value={currentProduct.unitSize}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, unitSize: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                    placeholder="e.g., 3.5g, 1g, 100mg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity on Hand
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={currentProduct.qoh}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, qoh: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Enter quantity"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={currentProduct.status}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, status: e.target.value as 'active' | 'inactive' })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Images
                </label>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {currentProduct.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Product ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {currentProduct.images.length < 4 && (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-green-500 transition-colors"
                      >
                        <div className="text-center p-4">
                          <Upload className="w-8 h-8 mx-auto text-gray-400" />
                          <p className="mt-2 text-sm text-gray-500">Upload Image</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <p className="text-sm text-gray-500">
                    Upload up to 4 images. Click on an image to remove it.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Exclusive Product</label>
                  <button
                    type="button"
                    onClick={() => setCurrentProduct(prev => ({ 
                      ...prev, 
                      isExclusive: !prev.isExclusive,
                      exclusiveCustomers: !prev.isExclusive ? prev.exclusiveCustomers : []
                    }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      currentProduct.isExclusive ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        currentProduct.isExclusive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {currentProduct.isExclusive && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Exclusive Customers
                    </label>
                    <div className="space-y-2">
                      {customers.map(customer => (
                        <label key={customer.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={currentProduct.exclusiveCustomers.includes(customer.id)}
                            onChange={(e) => {
                              const newExclusiveCustomers = e.target.checked
                                ? [...currentProduct.exclusiveCustomers, customer.id]
                                : currentProduct.exclusiveCustomers.filter(id => id !== customer.id);
                              setCurrentProduct({ ...currentProduct, exclusiveCustomers: newExclusiveCustomers });
                            }}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-gray-700">{customer.companyName}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center mt-6">
                {/* Delete button - only show for admins when editing existing product */}
                {currentProduct.id && isAdmin() ? (
                  <button
                    type="button"
                    onClick={() => handleDeleteProduct(currentProduct.id)}
                    className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete product"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                ) : (
                  <div></div>
                )}

                {/* Cancel and Save buttons */}
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={closeForm}
                    className="px-4 py-2 border rounded-md hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    {currentProduct.id ? 'Update' : 'Add'} Product
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && productToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Header with icon */}
            <div className="flex items-start space-x-4 p-6 pb-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Product
                </h3>
                <p className="text-gray-600 mb-3">
                  Are you sure you want to delete{' '}
                  <span className="font-semibold">"{productToDelete.product}"</span>?
                </p>
              </div>
            </div>

            {/* Warning message */}
            <div className="px-6 pb-4">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> If this product has order history, it will be set to inactive instead of being deleted to preserve order records.
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium flex items-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Product</span>
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Products;